import { Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import { AudioDictationService } from './audio-dictation.service';

export interface MedicalNecessityPdfParams {
  item: any;
  patientId: string;
  patientName: string;
  patientMrn: string;
  providerName: string;
}

/**
 * Generates the "Medical Necessity Coverage Report" PDF for an action item.
 * Single source of truth so every place that offers this download (transcription
 * modal, live operations dashboard, ...) produces the exact same document.
 */
@Injectable({
  providedIn: 'root',
})
export class MedicalNecessityPdfService {
  constructor(private audioDictationService: AudioDictationService) {}

  async generate(params: MedicalNecessityPdfParams): Promise<void> {
    const { item, patientId, patientName, patientMrn, providerName } = params;

    const resp = await this.audioDictationService.getSixMonthsOrtMasters(patientId);
    const patientNotes: any[] = Array.isArray(resp?.data?.ortMasters) ? resp.data.ortMasters : [];
    const order = item?.order;

    const pdf   = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const mX    = 15;   // horizontal margin
    const mY    = 18;   // vertical margin
    const cW    = pageW - mX * 2;
    const lh    = 5.5;  // base line height mm
    let   y     = mY;

    // ── Helpers ───────────────────────────────────────────────────────────
    const guard = (need: number) => {
      if (y + need > pageH - mY) { pdf.addPage(); y = mY; }
    };

    const pdfTitle = (text: string) => {
      pdf.setFontSize(18); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(10, 36, 99);
      const ls = pdf.splitTextToSize(text, cW);
      guard(ls.length * 8 + 6);
      pdf.text(ls, mX, y); y += ls.length * 8 + 1;
      pdf.setDrawColor(10, 36, 99); pdf.setLineWidth(0.6);
      pdf.line(mX, y, pageW - mX, y); y += 6;
      pdf.setTextColor(0, 0, 0);
    };

    const pdfSection = (text: string) => {
      guard(55); y += 2;
      pdf.setFontSize(11); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(255, 255, 255);
      pdf.setFillColor(10, 36, 99);
      pdf.roundedRect(mX - 2, y - 5.5, cW + 4, 8, 1.5, 1.5, 'F');
      pdf.text(text.toUpperCase(), mX + 1, y); y += 8 + 3;
      pdf.setTextColor(0, 0, 0);
    };

    const pdfSub = (text: string) => {
      guard(20);
      pdf.setFontSize(10); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30, 60, 130);
      const ls = pdf.splitTextToSize(text, cW);
      pdf.text(ls, mX, y); y += ls.length * 6 + 2;
      pdf.setTextColor(0, 0, 0);
    };

    const pdfLabelVal = (label: string, value: string) => {
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      const lw = pdf.getTextWidth(`${label}: `);
      pdf.setFont('helvetica', 'normal');
      const vl = pdf.splitTextToSize(value || '—', cW - lw);
      if (y + lh > pageH - mY) { pdf.addPage(); y = mY; }
      pdf.setFont('helvetica', 'bold'); pdf.setTextColor(60, 60, 60);
      if (vl.length === 1 && lw + pdf.getTextWidth(vl[0]) <= cW) {
        pdf.text(`${label}:`, mX, y);
        pdf.setFont('helvetica', 'normal'); pdf.setTextColor(30, 30, 30);
        pdf.text(vl[0], mX + lw, y); y += lh + 1;
      } else {
        pdf.text(`${label}:`, mX, y); y += lh;
        pdf.setFont('helvetica', 'normal'); pdf.setTextColor(30, 30, 30);
        vl.forEach((line: string) => {
          if (y + lh > pageH - mY) { pdf.addPage(); y = mY; }
          pdf.text(line, mX + 4, y); y += lh;
        });
        y += 1;
      }
      pdf.setTextColor(0, 0, 0);
    };

    const pdfPara = (text: string, indent = 0) => {
      pdf.setFontSize(10); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(30, 30, 30);
      const ls = pdf.splitTextToSize(text, cW - indent);
      ls.forEach((line: string) => {
        if (y + lh > pageH - mY) { pdf.addPage(); y = mY; }
        pdf.text(line, mX + indent, y); y += lh;
      });
      y += 1;
      pdf.setTextColor(0, 0, 0);
    };

    const pdfBullet = (text: string) => {
      pdf.setFontSize(10); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(30, 30, 30);
      const ls = pdf.splitTextToSize(`•  ${text}`, cW - 5);
      ls.forEach((line: string) => {
        if (y + lh > pageH - mY) { pdf.addPage(); y = mY; }
        pdf.text(line, mX + 5, y); y += lh;
      });
      y += 1;
      pdf.setTextColor(0, 0, 0);
    };

    const writeTable = (table: Element) => {
      const rows = Array.from(table.querySelectorAll('tr'));
      if (!rows.length) return;

      const tableData: string[][] = rows.map(row =>
        Array.from(row.querySelectorAll('th, td'))
          .map(c => (c.textContent ?? '').replace(/\s+/g, ' ').trim())
      );
      const numCols = Math.max(...tableData.map(r => r.length));
      if (numCols === 0) return;

      const fSize  = 9;
      // Proportional column widths based on max content length per column
      const colMaxLen = Array.from({ length: numCols }, (_, ci) =>
        Math.max(1, ...tableData.map(r => (r[ci] ?? '').length))
      );
      const totalLen = colMaxLen.reduce((s, l) => s + l, 0);
      const colWidths = colMaxLen.map(l => (l / totalLen) * cW);
      const cellPad = 2;    // mm padding inside each cell
      const rowLh  = 4.5;  // line height for 9pt text
      let tableTop = y;

      pdf.setFontSize(fSize);

      tableData.forEach((cells, ri) => {
        const isHeader = ri === 0;

        // Pre-split each cell into lines using proportional column widths
        const cellLines: string[][] = Array.from({ length: numCols }, (_, ci) =>
          pdf.splitTextToSize(cells[ci] ?? '', colWidths[ci] - cellPad * 2)
        );
        const maxLines = Math.max(1, ...cellLines.map(l => l.length));
        const rowH = maxLines * rowLh + cellPad * 2;

        // Page break — close current table border, start fresh
        if (y + rowH > pageH - mY) {
          pdf.setDrawColor(10, 36, 99); pdf.setLineWidth(0.4);
          pdf.rect(mX, tableTop, cW, y - tableTop, 'S');
          pdf.addPage(); y = mY; tableTop = y;
        }

        const rowTop = y;

        // Row background
        if (isHeader) {
          pdf.setFillColor(10, 36, 99);
        } else if (ri % 2 === 1) {
          pdf.setFillColor(243, 246, 255);
        } else {
          pdf.setFillColor(255, 255, 255);
        }
        pdf.rect(mX, rowTop, cW, rowH, 'F');

        // Cell text
        pdf.setFontSize(fSize);
        if (isHeader) {
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(255, 255, 255);
        } else {
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(40, 40, 40);
        }
        let xCursor = mX;
        cellLines.forEach((lines, ci) => {
          lines.forEach((line, li) => {
            pdf.text(line, xCursor + cellPad, rowTop + cellPad + (li + 1) * rowLh - 0.5);
          });
          xCursor += colWidths[ci];
        });

        // Vertical column separators using proportional widths
        pdf.setDrawColor(isHeader ? 30 : 200, isHeader ? 60 : 215, isHeader ? 130 : 240);
        pdf.setLineWidth(0.15);
        let sepX = mX;
        for (let ci = 1; ci < numCols; ci++) {
          sepX += colWidths[ci - 1];
          pdf.line(sepX, rowTop, sepX, rowTop + rowH);
        }

        // Horizontal row separator (skip after last row — handled by outer border)
        if (ri < tableData.length - 1) {
          pdf.setDrawColor(200, 215, 240); pdf.setLineWidth(0.15);
          pdf.line(mX, rowTop + rowH, mX + cW, rowTop + rowH);
        }

        y = rowTop + rowH;
      });

      // Outer border
      pdf.setDrawColor(10, 36, 99); pdf.setLineWidth(0.4);
      pdf.rect(mX, tableTop, cW, y - tableTop, 'S');

      y += 4;
      pdf.setTextColor(0, 0, 0);
    };

    const writeNode = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const t = (node.textContent ?? '').replace(/\s+/g, ' ').trim();
        if (t) pdfPara(t);
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const el  = node as Element;
      const tag = el.tagName.toLowerCase();
      if (tag === 'head' || tag === 'style') return;
      switch (tag) {
        case 'h1': {
          const t = el.textContent?.trim() ?? '';
          if (t) {
            guard(16);
            y += 4;
            pdf.setFontSize(15); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(10, 36, 99);
            const ls = pdf.splitTextToSize(t, cW);
            pdf.text(ls, mX, y);
            y += ls.length * 7 + 1;
            pdf.setDrawColor(10, 36, 99); pdf.setLineWidth(0.5);
            pdf.line(mX, y, pageW - mX, y);
            y += 6;
            pdf.setTextColor(0, 0, 0);
          }
          break;
        }
        case 'h2': {
          const t = el.textContent?.trim() ?? '';
          if (t) {
            guard(16);
            y += 4;
            pdf.setFontSize(10); pdf.setFont('helvetica', 'bold');
            pdf.setFillColor(225, 232, 252);
            pdf.roundedRect(mX - 2, y - 5.5, cW + 4, 7.5, 1, 1, 'F');
            pdf.setTextColor(10, 36, 99);
            pdf.text(t, mX + 1, y);
            y += 8 + 2;
            pdf.setTextColor(0, 0, 0);
          }
          break;
        }
        case 'p': {
          const t = el.textContent?.replace(/\s+/g, ' ').trim() ?? '';
          if (t) {
            const isFootnote = t.startsWith('Electronically signed') ||
                               t.startsWith('Document generated') ||
                               t.startsWith('A Note To Patients') ||
                               t.startsWith('Portions of the record');
            if (isFootnote) {
              guard(lh + 2);
              y += 2;
              pdf.setFontSize(8); pdf.setFont('helvetica', 'italic'); pdf.setTextColor(120, 120, 120);
              const ls = pdf.splitTextToSize(t, cW);
              ls.forEach((line: string) => {
                if (y + lh > pageH - mY) { pdf.addPage(); y = mY; }
                pdf.text(line, mX, y); y += lh - 0.5;
              });
              y += 1;
              pdf.setTextColor(0, 0, 0);
            } else {
              pdfPara(t);
            }
          }
          break;
        }
        case 'li': { const t = el.textContent?.replace(/\s+/g, ' ').trim() ?? ''; if (t) pdfBullet(t); break; }
        case 'table': y += 2; writeTable(el); break;
        case 'section': y += 3; Array.from(el.childNodes).forEach(c => writeNode(c)); y += 4; break;
        default:   Array.from(el.childNodes).forEach(c => writeNode(c));
      }
    };

    // ── Build document ─────────────────────────────────────────────────────
    pdfTitle('Medical Necessity Coverage Report');

    pdfSection('Patient Information');
    if (patientName) pdfLabelVal('Patient Name', patientName);
    if (patientMrn)  pdfLabelVal('MRN', patientMrn);
    if (providerName) pdfLabelVal('Provider', providerName);

    if (item?.action_item) {
      pdfSection('Action Item');
      pdfPara(item.action_item);
    }

    pdfSection('Medical Necessity');
    if (order?.lcd_coverage) pdfPara(order.lcd_coverage);
    let details: any = order?.lcd_coverage_details;
    if (details) {
      if (typeof details === 'string') { try { details = JSON.parse(details); } catch { /* keep as string */ } }
      y += 2; pdfSub('Coverage Details');
      if (typeof details === 'string') {
        pdfPara(details);
      } else if (typeof details === 'object' && details !== null) {
        for (const key of Object.keys(details)) {
          const val = details[key]; if (val == null) continue;
          pdfLabelVal(key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), typeof val === 'object' ? JSON.stringify(val) : String(val));
        }
      }
    }

    if (patientNotes.length) {
      pdfSection(`Patient Notes  (${patientNotes.length} Record${patientNotes.length > 1 ? 's' : ''})`);
      patientNotes.forEach((note: any, idx: number) => {
        const noteDate = note.date ? new Date(note.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
        const noteTime = note.time ? `  ${note.time}${note.period ? ' ' + note.period : ''}` : '';
        pdfSub(`Note ${idx + 1}  ·  ${noteDate}${noteTime}`);
        if (note.master_notes) {
          const htmlDoc = new DOMParser().parseFromString(note.master_notes, 'text/html');
          writeNode(htmlDoc.body);
        }
      });
    }

    // ── Page numbers ───────────────────────────────────────────────────────
    const totalPages = pdf.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      pdf.setPage(p);
      pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(160, 160, 160);
      pdf.text(`Page ${p} of ${totalPages}`, pageW / 2, pageH - 6, { align: 'center' });
      pdf.text('Medical Necessity Coverage Report', mX, pageH - 6);
    }

    const ref = order?.order_number ?? order?.order_id ?? 'report';
    pdf.save(`medical-necessity-${ref}.pdf`);
  }
}
